"use client";

import cn from "@utils/cn";
import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { FC, useEffect, useState } from "react";
import SwiperCore from "swiper";
// import "swiper/css";
// import "swiper/css/navigation";
// import "swiper/css/pagination";
// import "swiper/css/zoom";
import { Navigation, Pagination, Zoom } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import useMediaQuery from "@/hooks/useMediaQuery";
import { TileCarouselCardProps, TileCarouselProps } from "@/types";

import { CardCarouselBottomNavigation } from "../AutogeneratedCarousel/CardCarouselBottomNavigation";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import Icon from "../ui/Icon/Icon";
import "./TileCarousel.css";
import TileCarouselCard from "./TileCarouselCard";
import TileCarouselContent from "./TileCarouselContent";

const Modal = dynamic(() => import("../ui/Modal/Modal"), { ssr: false });
const ModalContent = dynamic(() => import("../ui/Modal/ModalContent"), { ssr: false });

SwiperCore.use([Pagination, Navigation, Zoom]);
export default function TileCarousel({ entry }: TileCarouselProps) {
  const locale = useLocale();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isLgDesktop = useMediaQuery("(min-width: 1500px");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<TileCarouselCardProps | null>(null);
  const [centeredSlides, setCenteredSlides] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const t = useTranslations();
  const isSo = useIsSaleScreen();
  const visibleCards = showAll ? entry.cards : entry.cards.slice(0, 24);

  const openModal = (card: TileCarouselCardProps) => {
    setSelectedCard(card);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedCard(null);
    setModalOpen(false);
  };

  const pathname = usePathname();

  const isHomePage = pathname === "/" || pathname === `/${locale}/`;
  if (isSo && isHomePage) {
    return null;
  }

  if (entry?.showInCardMode && entry.cards.length >= 1) {
    return (
      <div
        className=" w-full max-w-screen-xl mx-auto"
        style={{ paddingLeft: `${isMobile ? "10px" : "70px"}`, paddingRight: `${isMobile ? "10px" : "70px"}` }}
      >
        <div className="relative mt-l">
          <div className={`grid md:grid-cols-4 gap-l`}>
            {visibleCards.map((card, index) => (
              <TileCarouselCard
                key={index}
                item={card}
                openModal={() => openModal(card)}
                showInCardMode={entry?.showInCardMode}
                title={entry.title}
              />
            ))}
          </div>
          {!showAll && entry.cards.length > 24 && (
            <div className="mt-6 text-left">
              <button onClick={() => setShowAll(true)} className="text-blue-600 underline">
                {t("show.more")}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <Swiper
        navigation={{
          nextEl: `.swiper-button-next-${entry.id}-tile`,
          prevEl: `.swiper-button-prev-${entry.id}-tile`,
        }}
        pagination={{ type: "progressbar", el: `.swiper-pagination-carousel-${entry.id}-tile` }}
        className={"tile-carousel-wrapper w-full !pt-[40px]"}
        breakpoints={{
          1024: {
            slidesPerView: "auto",
            spaceBetween: 24,
            centerInsufficientSlides: true,
          },
          1366: {
            slidesPerView: "auto",
            spaceBetween: 24,
            centerInsufficientSlides: true,
          },
          1500: {
            slidesPerView: "auto",
            spaceBetween: 24,
            centerInsufficientSlides: true,
          },
        }}
        spaceBetween={10}
        slidesPerView={1.5}
        centeredSlides={isMobile && centeredSlides}
        centerInsufficientSlides={true}
        watchSlidesProgress={true}
        slidesOffsetAfter={entry.cards.length > 4 || isMobile ? 20 : 0}
        onSlideChange={(swiper) => {
          const lastSlideIndex = entry.cards.length - 1;

          if (swiper.activeIndex === 0 || swiper.activeIndex === lastSlideIndex) {
            setCenteredSlides(false);
          } else if (!centeredSlides) {
            setCenteredSlides(true);
          }
        }}
      >
        {entry.cards.map((card, index) => (
          <SwiperSlide
            className={cn(
              entry.cards.length <= 4 ? "tile-carousel-slide" : "first:md:!ml-[24px]",
              isLgDesktop && entry.cards.length <= 5 && "first:md:!ml-[66px]",
            )}
            key={`tile-carousel-${index}`}
          >
            <TileCarouselCard item={card} openModal={() => openModal(card)} title={entry.title} />
          </SwiperSlide>
        ))}
      </Swiper>

      {entry.cards.length > 2 && (
        <div className="w-full">
          <CardCarouselBottomNavigation
            carouselId={`${entry.id}-tile`}
            customClass={
              !isMobile
                ? entry.cards.length < 5 || (isLgDesktop && entry.cards.length <= 5)
                  ? "hidden"
                  : "md:ml-[calc(100%_*_2_/_22)] 2xl:ml-[calc(100%_*_2_/_18)] md:w-[77%] pb-xl mt-m tile-carousel-pagination"
                : "pb-xl mt-l md:mt-m"
            }
          />
        </div>
      )}

      {entry.cta && (
        <div className={cn("w-full mx-auto text-center pb-xl", !isMobile && entry.cards.length <= 4 && "pt-xl")}>
          <Button
            href={getLink(entry.cta, locale)}
            customClass="block w-fit mx-auto"
            size={ButtonSize.MEDIUM}
            theme={ButtonTheme.BLACK}
            type="button"
            variant={ButtonVariant.SECONDARY}
          >
            {entry.cta.label}
          </Button>
        </div>
      )}
      {selectedCard?.primaryCta && (
        <Modal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
          }}
        >
          <ModalContent className="h-dvh w-dvw !rounded-none pt-0 md:pt-m !px-0 md:!px-l">
            <div className={cn("flex items-center justify-between mb-l px-m md:px-0 pt-l")}>
              <p className={cn("font-primary uppercase")}>{selectedCard?.title}</p>
              <Button
                className={"stroke-black"}
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.TERTIARY}
                theme={ButtonTheme.BLACK}
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                <span className="pointer-events-none">
                  <Icon name="close" size="24" title={t("label.common.close")} />
                  <span className="sr-only">{t("button.label.closeModal")}</span>
                </span>
              </Button>
            </div>
            {
              <iframe
                className={cn("object-cover w-full h-[calc(100dvh-72px)] md:rounded-lg border-0")}
                width="560"
                height="315"
                src={
                  (typeof selectedCard?.primaryCta === "object" &&
                    "embeddedVideo" in selectedCard.primaryCta &&
                    selectedCard?.primaryCta?.embeddedVideo?.link?.url) ||
                  ""
                }
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
              ></iframe>
            }
          </ModalContent>
        </Modal>
      )}

      {selectedCard && (
        <Modal open={modalOpen} onClose={closeModal}>
          <ModalContent className={selectedCard.modal?.layout === "full-screen" ? "w-dvw md:w-full h-dvh md:rounded-none !px-0" : ""}>
            <TileCarouselContent card={selectedCard} onClose={closeModal} />
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
